import React, { FC } from 'react';
import { Button, Modal } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import generateTestId from 'packages/helpers/testIdHelpers';
import modalContainer from 'packages/common/modalContainer';

type PublishModalProps = {
    variationCount: number;
    open: boolean;
    loading: boolean;
    isDimensionInvalid: boolean;
    onClose: () => void;
    onPublish: () => void;
};

const PublishModal: FC<PublishModalProps> = ({
    variationCount,
    open,
    loading,
    isDimensionInvalid,
    onClose,
    onPublish,
}) => {
    const { translate } = useTranslate();

    const actionId = generateTestId('editor_header_publish_action');
    const noActionId = generateTestId('editor_header_no_publish_action');
    const cancelId = generateTestId('editor_header_publish_cancel');
    const modalTitle = !isDimensionInvalid
        ? translate('editor.header.publish.modal.title')
        : translate('editor.header.publish.modal.no-publish.title');

    const modalDescription = !isDimensionInvalid
        ? translate('editor.header.publish.modal.desc', { count: variationCount })
        : translate('editor.header.publish.modal.no-publish.desc');

    if (!open) {
        return null;
    }

    return (
        <Modal
            container={modalContainer}
            title={modalTitle}
            isCloseButtonHidden={loading}
            onClose={onClose}
            isOpen={open}
            actionSecondary={
                !isDimensionInvalid ? (
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        isDisabled={loading}
                        {...cancelId}
                        {...{ id: 'editor_header_publish_cancel' }}
                    >
                        {translate('editor.header.publish.modal.cancel')}
                    </Button>
                ) : null
            }
            actionPrimary={
                !isDimensionInvalid ? (
                    <Button
                        variant="primary"
                        onClick={onPublish}
                        isLoading={loading}
                        isDisabled={loading}
                        {...actionId}
                        {...{ id: 'editor_header_publish_action' }}
                    >
                        {loading
                            ? translate('editor.header.publish.modal.publishing')
                            : translate('editor.header.publish.modal.publish')}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={onClose}
                        {...noActionId}
                        {...{ id: 'editor_header_no_publish_action' }}
                    >
                        {translate('editor.header.publish.modal.no-publish.okay')}
                    </Button>
                )
            }
        >
            {modalDescription}
        </Modal>
    );
};

export default React.memo(PublishModal);
